import { Form, Formik } from 'formik';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import * as Yup from 'yup';

import { Common } from '@components/Admin';

import useCommonSchemas from '@hooks/schemas/useCommonSchemas';

import { formatDate } from '@utils/format';

import { useAdminUpdateRole } from 'generated/admin.generated';
import type { RoleModel, AccountType } from 'generated/types';

interface MetadataEditorProps {
  role?: RoleModel;
  loading?: boolean;
}

const messages = defineMessages({
  name: {
    id: 'admin.roles.name',
    defaultMessage: 'Name',
  },
  description: {
    id: 'admin.roles.description',
    defaultMessage: 'Description',
  },
  rank: {
    id: 'admin.roles.rank',
    defaultMessage: 'Rank',
  },
  scope: {
    id: 'admin.roles.scope',
    defaultMessage: 'Scope',
  },
  updatedAt: {
    id: 'GLOBAL.updatedAt',
    defaultMessage: 'Updated at',
  },
  createdAt: {
    id: 'GLOBAL.createdAt',
    defaultMessage: 'Created At',
  },
  required: {
    id: 'GLOBAL.required',
    defaultMessage: 'This field is required',
  },
});

interface FormValues {
  name: string;
  description?: string | null;
  scope?: AccountType | null;
  rank?: number | null;
}

function MetadataEditor({ role, loading: baseLoading }: MetadataEditorProps) {
  const { formatMessage } = useIntl();
  const [updateRole, { loading: updateRoleLoading }] = useAdminUpdateRole();
  const { AdminSchema } = useCommonSchemas();

  const loading = baseLoading || updateRoleLoading;

  const initialValues: FormValues = {
    name: role?.role_name ?? '',
    description: role?.role_description,
    rank: role?.rank,
    scope: role?.scope,
  };

  const handleSubmit = async (values: FormValues) => {
    if (!role) {
      return;
    }
    await updateRole({
      variables: {
        input: {
          role_id: role.role_id,
          rank: values.rank ?? undefined,
          scope: values.scope ?? undefined,
        },
      },
    });
  };

  const schema: Yup.Schema<FormValues> = Yup.object().shape({
    name: AdminSchema.name().required(formatMessage(messages.required)),
    description: AdminSchema.description().nullable(),
    rank: Yup.number().integer().nullable(),
    scope: AdminSchema.roleScope.nullable(),
  });

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={handleSubmit}
      validationSchema={schema}
    >
      {({ values }) => {
        const data: Common.Card.IMetadata.Metadata[] = [
          {
            title: formatMessage(messages.scope),
            value: values.scope,
            editOptions: { name: 'scope', type: 'text' },
          },
          {
            title: formatMessage(messages.name),
            value: values.name,
            editOptions: { name: 'name', type: 'text' },
          },
          {
            title: formatMessage(messages.createdAt),
            value: formatDate({
              date: role?.created_at,
              formatter: 'datetime',
            }),
          },
          {
            title: formatMessage(messages.updatedAt),
            value: formatDate({
              date: role?.updated_at,
              formatter: 'datetime',
            }),
          },
        ];
        return (
          <Form>
            <Common.Card.Metadata data={data} loading={loading} />
          </Form>
        );
      }}
    </Formik>
  );
}

export default MetadataEditor;
